import * as React from 'react';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import { visuallyHidden } from '@mui/utils';
import { Button, Grid, TextField } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ArchiveIcon from '@mui/icons-material/Archive';
import { useSelector } from 'react-redux';
import { IReduxState } from '../../store';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';

interface Data {
    id: number;
    destinataire: string;
    title: number;
    message: number;
}


function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string },
) => number {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
// function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
//     const stabilizedThis = array?.map((el, index) => [el, index] as [T, number]);
//     stabilizedThis.sort((a, b) => {
//         const order = comparator(a[0], b[0]);
//         if (order !== 0) {
//             return order;
//         }
//         return a[1] - b[1];
//     });
//     return stabilizedThis.map((el) => el[0]);
// }

interface HeadCell {
    disablePadding: boolean;
    id: string;
    label: string;
    numeric: boolean;
}



interface EnhancedTableProps {
    numSelected: number;
    onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
    onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
    order: Order;
    orderBy: string;
    rowCount: number;
    filteredEtabs: any;
}

function EnhancedTableHead(props: EnhancedTableProps) {
    const { onSelectAllClick, order, orderBy, numSelected, filteredEtabs, rowCount, onRequestSort } =
        props;

    const headCells: readonly HeadCell[] = [
        {
            id: 'codeEtab',
            numeric: false,
            disablePadding: true,
            label: `Etablissement selectionné(s): (${numSelected} / ${filteredEtabs.length})`,
        }
    ];

    const createSortHandler =
        (property: any) => (event: React.MouseEvent<unknown>) => {
            onRequestSort(event, property);
        };

    return (
        <TableHead>
            <TableRow>
                <TableCell padding="checkbox">
                    <Checkbox
                        color="primary"
                        indeterminate={numSelected > 0 && numSelected < rowCount}
                        checked={rowCount > 0 && numSelected === rowCount}
                        onChange={onSelectAllClick}
                        inputProps={{
                            'aria-label': 'select all desserts',
                        }}
                    />
                </TableCell>
                {headCells?.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={"left"}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

interface EnhancedTableToolbarProps {
    numSelected: number;
    search: any;
    setSearch: any;
    handleChange: any;
    value: any;
}

function EnhancedTableToolbar(props: EnhancedTableToolbarProps) {
    const { numSelected, search, setSearch, handleChange, value } = props;

    return (
        <Toolbar
            sx={{
                pl: { sm: 2 },
                pr: { xs: 1, sm: 1 },
                ...(numSelected > 0 && {
                    bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
                }),
            }}
        >
            <Grid
                container
                spacing={2}
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
            >
                <Grid item md={12} xs={12} sm={12}>
                    <FormControl>
                        <RadioGroup
                            row
                            value={value}
                            onChange={handleChange}
                        >
                            <FormControlLabel value={1} control={<Radio />} label="Tout" />
                            <FormControlLabel value={2} control={<Radio />} label="Individuel" />
                        </RadioGroup>
                    </FormControl>
                </Grid>
                {value == 1 ? "" :
                    <Grid item md={12} xs={12} sm={12}>
                        <TextField
                            style={{ marginBottom: 5 }}
                            fullWidth
                            size="small"
                            name="search"
                            label="Recherche"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </Grid>}
            </Grid>
        </Toolbar >
    );
}

export interface IListeEtabProps {
    selected: any;
    setSelected: (x: any) => void
}

export default function ListeEtab(props: IListeEtabProps) {
    const { selected, setSelected } = props

    const etabs = useSelector((state: IReduxState) => state.focusEcole.etabs);

    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<keyof Data>('message');
    const [page, setPage] = React.useState(0);
    const [dense, setDense] = React.useState(false);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);

    const [value, setValue] = React.useState(1);

    const handleChange = (event: any) => {
        setValue((event.target as any).value);
    };

    //---------------------------------------------//
    const [search, setSearch] = React.useState('');
    const [filteredEtabs, setFilteredEtabs] = React.useState([...etabs]);

    const filterFunction = () => {
        if (search.length === 0) {
            setFilteredEtabs([...etabs]);
        } else {
            const result = etabs.filter((item: any) => {
                const value = `${item.codeEtab} ${item.libEtab}`;
                return value.toLowerCase().includes(search.toLowerCase());
            });
            setFilteredEtabs(result);
        }
    };

    React.useEffect(() => {
        filterFunction();
    }, [search, etabs]);
    //--------------------------------------------//

    const handleRequestSort = (
        event: React.MouseEvent<unknown>,
        property: keyof Data,
    ) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelected = filteredEtabs.map((n: any) => n.codeEtab)
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event: React.MouseEvent<unknown>, id: number) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected: readonly number[] = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }
        setSelected(newSelected);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleChangeDense = (event: React.ChangeEvent<HTMLInputElement>) => {
        setDense(event.target.checked);
    };

    const isSelected = (id: number) => selected.indexOf(id) !== -1;

    // Avoid a layout jump when reaching the last page with empty etabs.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredEtabs.length) : 0;


    return (
        <Box sx={{ width: '100%' }}>
            <Paper sx={{ width: '100%', mb: 2, backgroundColor: "#fcfafc", height: "50vh", overflow: "auto" }}>
                <EnhancedTableToolbar
                    numSelected={selected.length}
                    search={search}
                    setSearch={setSearch}
                    handleChange={handleChange}
                    value={value}
                />
                {value == 1
                    ? (<Typography variant="body2" gutterBottom style={{ color: "GrayText", textAlign: "justify", margin: "20px" }}>
                        Si vous choisissez l'option 'Tout', votre message sera envoyé à tous les utilisateurs de l'application sélectionnée en haut. Cela signifie que chaque utilisateur inscrit à cette application recevra le message, peu importe leur statut ou leur rôle. Assurez-vous que le contenu du message est approprié pour une diffusion large et qu'il contient les informations nécessaires pour tous les utilisateurs. Cette option est idéale pour les annonces générales, les mises à jour importantes ou les notifications qui concernent l'ensemble des utilisateurs.
                        <br /><br />
                        Si votre message est destiné à un groupe spécifique ou à un individu en particulier, veuillez choisir l'option 'Individuel' afin de cibler plus précisément votre audience. Cela permet d'éviter la surcharge d'informations pour les utilisateurs non concernés et d'assurer que le message est pertinent pour le destinataire. Pensez à vérifier le contenu et la pertinence du message avant de le valider pour l'envoi. Merci de votre attention et de votre coopération pour maintenir une communication claire et efficace.
                    </Typography>
                    )
                    : <div style={{ height: "50vh", overflow: "auto" }}>
                        <TableContainer>
                            <Table
                                sx={{ minWidth: 750 }}
                                aria-labelledby="tableTitle"
                                size={"small"}
                            >
                                <EnhancedTableHead
                                    numSelected={selected.length}
                                    order={order}
                                    orderBy={orderBy}
                                    onSelectAllClick={handleSelectAllClick}
                                    onRequestSort={handleRequestSort}
                                    rowCount={filteredEtabs.length}
                                    filteredEtabs={filteredEtabs}
                                />
                                <TableBody>
                                    {
                                        filteredEtabs.map((row: any, index: number) => {
                                            const isItemSelected = isSelected(row.codeEtab);
                                            const labelId = `enhanced-table-checkbox-${index}`;
                                            return (
                                                <TableRow
                                                    hover
                                                    onClick={(event) => handleClick(event, row.codeEtab)}
                                                    role="checkbox"
                                                    aria-checked={isItemSelected}
                                                    tabIndex={-1}
                                                    key={row.codeEtab}
                                                    selected={isItemSelected}
                                                    sx={{ cursor: 'pointer' }}
                                                >
                                                    <TableCell padding="checkbox">
                                                        <Checkbox
                                                            color="primary"
                                                            checked={isItemSelected}
                                                            inputProps={{
                                                                'aria-labelledby': labelId,
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell
                                                        component="th"
                                                        id={labelId}
                                                        scope="row"
                                                        padding="none"
                                                        style={{ color: "GrayText" }}
                                                    >
                                                        {`${row.codeEtab} ${row.libEtab}`}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    }
                                    {emptyRows > 0 && (
                                        <TableRow
                                            style={{
                                                height: (dense ? 33 : 53) * emptyRows,
                                            }}
                                        >
                                            <TableCell colSpan={6} />
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={filteredEtabs.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </div>}
            </Paper>
        </Box>
    );
}
